import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { RootState } from "@/store";

interface ProtectedRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
  requireAuth?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  redirectTo = "/auth/login",
  requireAuth = true,
}) => {
  const { isAuthenticated, isLoading, user } = useSelector(
    (state: RootState) => state.auth,
  );
  const navigate = useNavigate();

  useEffect(() => {
    // Don't redirect while loading
    if (isLoading) return;

    if (requireAuth && !isAuthenticated) {
      // Redirect to login if authentication is required but user is not authenticated
      navigate(redirectTo);
    } else if (!requireAuth && isAuthenticated && user) {
      // Redirect authenticated users away from auth pages to their dashboard
      const dashboardPath = getDashboardPath(user.role);
      navigate(dashboardPath);
    }
  }, [isAuthenticated, user, isLoading, requireAuth, navigate, redirectTo]);

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  // For protected routes, only render if authenticated
  if (requireAuth && !isAuthenticated) {
    return null;
  }

  // For auth routes, don't render if already authenticated (will redirect)
  if (!requireAuth && isAuthenticated) {
    return null;
  }

  return <>{children}</>;
};

// Helper function to determine dashboard path based on user role
const getDashboardPath = (role: string): string => {
  switch (role) {
    case "DeliveryDriver":
    case "ScooterDriver":
    case "VehicleDriver":
      return "/driver/dashboard";
    case "Fleet":
      return "/fleet/dashboard";
    case "Restaurant":
      return "/restaurant/dashboard";
    default:
      return "/driver/dashboard";
  }
};

export default ProtectedRoute;
